import { AlertCircle, CheckCircle, Loader2, Mail, XCircle } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { routePaths } from '@/lib/router'
import { authService } from '@/services/auth'

const emailSchema = z
  .string()
  .min(1, 'Email is required')
  .email('Please enter a valid email address')

type VerificationState =
  | 'loading'
  | 'success'
  | 'error'
  | 'invalid-token'
  | 'expired-token'

export function VerifyEmailPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [verificationState, setVerificationState] =
    useState<VerificationState>('loading')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [isResending, setIsResending] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState<string | null>(null)

  // Use a ref to prevent double-verification (synchronous check prevents race conditions)
  const verificationAttemptedRef = useRef(false)

  const token = searchParams.get('token')

  const handleResendVerification = async () => {
    // Validate email with zod
    const result = emailSchema.safeParse(email)

    if (!result.success) {
      setEmailError(result.error.issues[0].message)
      return
    }

    setEmailError(null)

    try {
      setIsResending(true)
      setResendSuccess(false)
      await authService.resendEmailVerification({ email: email.trim() })
      setResendSuccess(true)
    } catch (error) {
      const err = error as { message?: string }
      const errorMsg = err?.message || 'Failed to resend verification email'
      setErrorMessage(errorMsg)
    } finally {
      setIsResending(false)
    }
  }

  const getIcon = () => {
    switch (verificationState) {
      case 'loading':
        return <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      case 'success':
        return <CheckCircle className="w-8 h-8 text-green-600" />
      case 'error':
      case 'invalid-token':
      case 'expired-token':
        return <XCircle className="w-8 h-8 text-red-600" />
      default:
        return <AlertCircle className="w-8 h-8 text-orange-600" />
    }
  }

  const getTitle = () => {
    switch (verificationState) {
      case 'loading':
        return 'Verifying Your Email...'
      case 'success':
        return 'Email Verified Successfully!'
      case 'invalid-token':
        return 'Verification Link Issue'
      case 'expired-token':
        return 'Verification Link Expired'
      case 'error':
        return 'Verification Failed'
      default:
        return 'Email Verification'
    }
  }

  const getMessage = () => {
    switch (verificationState) {
      case 'loading':
        return 'Please wait while we verify your email address...'
      case 'success':
        return 'Your email address has been successfully verified. You can now access all features of your account.'
      case 'invalid-token':
        return 'This verification link is invalid or has already been used. Your email may have already been verified — try logging in. If not, you can request a new verification email below.'
      case 'expired-token':
        return 'The verification link has expired for security reasons. Please request a new verification email.'
      case 'error':
        return (
          errorMessage ||
          'An error occurred while verifying your email. Please try again or contact support.'
        )
      default:
        return 'Something went wrong during email verification.'
    }
  }

  const getBackgroundColor = () => {
    switch (verificationState) {
      case 'success':
        return 'bg-green-100 dark:bg-green-900/20'
      case 'loading':
        return 'bg-blue-100 dark:bg-blue-900/20'
      case 'error':
      case 'invalid-token':
      case 'expired-token':
        return 'bg-red-100 dark:bg-red-900/20'
      default:
        return 'bg-orange-100 dark:bg-orange-900/20'
    }
  }

  useEffect(() => {
    // Synchronous check prevents race conditions from double-renders (e.g., React Strict Mode)
    if (verificationAttemptedRef.current) return
    verificationAttemptedRef.current = true

    if (!token) {
      setVerificationState('invalid-token')
      return
    }

    const verifyEmail = async () => {
      try {
        setVerificationState('loading')
        await authService.verifyEmail({ token })
        setVerificationState('success')
      } catch (error) {
        const err = error as { message?: string }
        const errorMsg = err?.message || 'Verification failed'
        setErrorMessage(errorMsg)

        // Determine specific error type based on message
        if (errorMsg.toLowerCase().includes('expired')) {
          setVerificationState('expired-token')
        } else if (
          errorMsg.toLowerCase().includes('invalid') ||
          errorMsg.toLowerCase().includes('not found')
        ) {
          setVerificationState('invalid-token')
        } else {
          setVerificationState('error')
        }
      }
    }

    verifyEmail()
  }, [token])

  return (
    <AuthLayout
      title="Email Verification"
      subtitle="Confirming your email address"
      showBranding={false}
    >
      <Card className="w-full">
        <CardHeader className="text-center">
          <CardTitle className="flex items-center justify-center space-x-2">
            <Mail className="h-5 w-5" />
            <span>Email Verification</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Status Icon and Message */}
          <div className="text-center space-y-4">
            <div
              className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center ${getBackgroundColor()}`}
            >
              {getIcon()}
            </div>

            <div className="space-y-2">
              <h3 className="text-xl font-semibold">{getTitle()}</h3>
              <p className="text-muted-foreground">{getMessage()}</p>
            </div>
          </div>

          {/* Success Actions */}
          {verificationState === 'success' && (
            <div className="space-y-4">
              <Alert className="bg-green-50 border-green-200 dark:bg-green-900/10 dark:border-green-800">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <div className="text-green-800 dark:text-green-200">
                  <p className="font-medium">
                    Welcome to Vwam, your AI Shopping Assistant!
                  </p>
                  <p className="text-sm mt-1">
                    Your account is now fully activated and ready to use.
                  </p>
                </div>
              </Alert>

              <Button
                onClick={() => navigate(routePaths.login)}
                className="w-full"
                size="lg"
              >
                Continue to Login
              </Button>
            </div>
          )}

          {/* Error Actions */}
          {(verificationState === 'expired-token' ||
            verificationState === 'invalid-token' ||
            verificationState === 'error') && (
            <div className="space-y-4">
              {resendSuccess && (
                <Alert className="bg-green-50 border-green-200 dark:bg-green-900/10 dark:border-green-800">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <div className="text-green-800 dark:text-green-200">
                    <p className="font-medium">Verification email sent!</p>
                    <p className="text-sm mt-1">
                      Please check your inbox for a new verification link.
                    </p>
                  </div>
                </Alert>
              )}

              {/* Email input for resend */}
              <div className="space-y-2">
                <label
                  htmlFor="resend-email"
                  className="text-sm font-medium text-foreground"
                >
                  Enter your email to resend verification
                </label>
                <Input
                  id="resend-email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    if (emailError) setEmailError(null)
                  }}
                  disabled={isResending || resendSuccess}
                  className={emailError ? 'border-red-500' : ''}
                />
                {emailError && (
                  <p className="text-sm text-red-500">{emailError}</p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  onClick={handleResendVerification}
                  disabled={isResending || resendSuccess}
                  className="w-full"
                >
                  {isResending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : resendSuccess ? (
                    'Email Sent!'
                  ) : (
                    'Resend Email'
                  )}
                </Button>

                <Button
                  onClick={() => navigate(routePaths.login)}
                  className="w-full"
                >
                  Go to Login
                </Button>
              </div>
            </div>
          )}

          {/* Loading State */}
          {verificationState === 'loading' && (
            <div className="flex justify-center">
              <Button variant="outline" disabled>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Verifying...
              </Button>
            </div>
          )}

          {/* Help Section */}
          <div className="border-t pt-6">
            <div className="text-center space-y-3">
              <p className="text-sm text-muted-foreground">
                Need help with email verification?
              </p>
              <div className="flex flex-col sm:flex-row gap-2 text-sm">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-primary hover:underline"
                >
                  Contact Support
                </Button>
                <span className="hidden sm:inline text-muted-foreground">
                  •
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-primary hover:underline"
                >
                  FAQ
                </Button>
              </div>
            </div>
          </div>

          {/* Return to Registration */}
          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              Want to use a different email?{' '}
              <Link
                to={routePaths.register}
                className="text-primary hover:underline font-medium"
              >
                Create new account
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
